import React, { useEffect, useReducer, useRef, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button, IconButton, InputAdornment, makeStyles, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search, CloudUpload } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ClientModal from "../../components/ClientModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchField: { width: 250 },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CLIENTS") {
    const incoming = action.payload;
    const next = [...state];
    incoming.forEach(c => {
      const idx = next.findIndex(x => x.id === c.id);
      if (idx !== -1) next[idx] = c; else next.push(c);
    });
    return next;
  }
  if (action.type === "UPDATE_CLIENT") {
    const c = action.payload;
    const idx = state.findIndex(x => x.id === c.id);
    if (idx !== -1) { state[idx] = c; return [...state]; }
    return [c, ...state];
  }
  if (action.type === "DELETE_CLIENT") {
    return state.filter(x => x.id !== action.payload);
  }
  return state;
};

const Clients = () => {
  const classes = useStyles();
  const [clients, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState(null);
  const csvInput = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get("/clients", { params: { searchParam } })
      .then(({ data }) => { dispatch({ type: "LOAD_CLIENTS", payload: data }); setLoading(false); })
      .catch(err => { toastError(err); setLoading(false); });
  }, [searchParam]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("client", data => {
      if (data.action === "create" || data.action === "update")
        dispatch({ type: "UPDATE_CLIENT", payload: data.client });
      if (data.action === "delete")
        dispatch({ type: "DELETE_CLIENT", payload: data.clientId });
    });
    return () => socket.disconnect();
  }, []);

  const handleEdit = c => { setSelectedClient(c); setModalOpen(true); };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${deletingClient.id}`);
      dispatch({ type: "DELETE_CLIENT", payload: deletingClient.id });
      toast.success("Cliente removido!");
    } catch (err) { toastError(err); }
    setDeletingClient(null);
  };

  const handleCSV = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    const rows = lines.slice(1).map(line => {
      const [name, sccmCode] = line.split(";").map(v => v.trim().replace(/^"|"$/g, ""));
      return { name, sccmCode };
    });
    try {
      const { data } = await api.post("/clients/import", { rows });
      const msg = data.skipped > 0
        ? `${data.imported} importado(s), ${data.skipped} ignorado(s) por já existirem.`
        : `${data.imported} cliente(s) importado(s)!`;
      toast.success(msg);
      const { data: fresh } = await api.get("/clients");
      dispatch({ type: "LOAD_CLIENTS", payload: fresh });
    } catch (err) { toastError(err); }
    e.target.value = "";
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingClient && `Excluir cliente "${deletingClient.name}"?`}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Os contatos vinculados perderão o vínculo com este cliente.
      </ConfirmationModal>

      <ClientModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedClient(null); }}
        clientId={selectedClient?.id}
      />

      <input
        type="file"
        accept=".csv"
        ref={csvInput}
        style={{ display: "none" }}
        onChange={handleCSV}
      />

      <MainHeader>
        <Title>Clientes</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nome ou código..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUpload />}
            onClick={() => csvInput.current.click()}
          >
            Importar CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModalOpen(true)}
          >
            Novo Cliente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Código SCCM</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map(c => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.sccmCode || "—"}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEdit(c)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setDeletingClient(c); setConfirmOpen(true); }}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={3} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Clients;
